import api from './axios';

/* ══════════════════════════════════════════════════════════════
   QUIZ
══════════════════════════════════════════════════════════════ */

/* ── Get randomised quiz questions ─────────────────────────────
   GET /api/quiz
   params: { level, limit }
   returns: { success, data: [] }
─────────────────────────────────────────────────────────────── */
export const getQuizQuestions = async (params = { level: 'N5', limit: 10 }) => {
  const { data } = await api.get('/quiz', { params });
  return data;
};

/* ── Submit a completed quiz attempt ────────────────────────────
   POST /api/quiz/attempt
   body: { level, score, total, duration, answers }
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const submitQuizAttempt = async ({ level, score, total, duration, answers }) => {
  const { data } = await api.post('/quiz/attempt', { level, score, total, duration, answers });
  return data;
};


/* ══════════════════════════════════════════════════════════════
   PROGRESS  (SRS + user stats)
══════════════════════════════════════════════════════════════ */

/* ── Get user stats ─────────────────────────────────────────────
   GET /api/progress
   returns: { success, data: { xp, streak, level, totalCards, masteredCards, dueCount, recentQuizzes } }
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const getUserStats = async () => {
  const { data } = await api.get('/progress');
  return data;
};

/* ── Get SRS cards due for review ───────────────────────────────
   GET /api/progress/due
   params: { cardType, limit }
   cardType: 'vocabulary' | 'kanji'
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const getDueCards = async (params = { cardType: 'vocabulary', limit: 20 }) => {
  const { data } = await api.get('/progress/due', { params });
  return data;
};

/* ── Update card after review (SM-2) ────────────────────────────
   POST /api/progress/update
   body: { cardId, cardType, result, level }
   result: 'know' | 'unknown'
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const updateCardProgress = async ({ cardId, cardType, result, level }) => {
  const { data } = await api.post('/progress/update', { cardId, cardType, result, level });
  return data;
};

/* ── Get public announcements ───────────────────────────────────
   GET /api/announcements
   returns: { success, data: [] }
─────────────────────────────────────────────────────────────── */
export const getAnnouncements = async () => {
  const { data } = await api.get('/announcements');
  return data;
};

/* ── Get the streak calendar for a month ────────────────────────
   GET /api/progress/streak
   params: { month }  — 'YYYY-MM', defaults to the current month
   returns: { success, data: { month, streak, lastStudied, activeDates: [] } }
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const getStreakCalendar = async (params = {}) => {
  const { data } = await api.get('/progress/streak', { params });
  return data;
};

/* ── Register a completed study action ──────────────────────────
   POST /api/progress/activity
   Generic streak hook for flows with no dedicated backend mutation to hang
   it off (e.g. finishing a grammar exercise, which is localStorage-only).
   The four graded-content endpoints register this themselves server-side.
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const registerActivity = async () => {
  const { data } = await api.post('/progress/activity');
  return data;
};

/* ══════════════════════════════════════════════════════════════
   ROADMAP
══════════════════════════════════════════════════════════════ */

/* ── Get roadmap lesson completion ──────────────────────────────
   GET /api/roadmap
   Completion is computed server-side from real mastery data, so it
   can't be faked client-side.
   returns: { success, data: { threshold, lessons: { [id]: { done, have, need, auto, kind } } } }
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const getRoadmap = async () => {
  const { data } = await api.get('/roadmap');
  return data;
};

/* ── Mark a manual-only roadmap lesson complete ─────────────────
   POST /api/roadmap/complete
   Rejected by the server for mastery-gated lessons.
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const completeRoadmapLesson = async (lessonId, done = true) => {
  /* Called both ways in the app: completeRoadmapLesson('n5-practice') and
     completeRoadmapLesson({ lessonId, done }). The object form was posting
     { lessonId: { lessonId: 'n5-practice', done: true } }, which the server
     rejected as an unknown lesson — so ticking off a manual step from the
     path silently did nothing. Normalise instead of picking a winner. */
  const args = (lessonId && typeof lessonId === 'object')
    ? { lessonId: lessonId.lessonId, done: lessonId.done !== false }
    : { lessonId, done };
  const { data } = await api.post('/roadmap/complete', args);
  return data;
};

/* ── Bank one stage of a staged lesson ──────────────────────────
   POST /api/roadmap/stage
   The server owns the pass mark; we only report what was scored.
   Resolves to { passed, required, stagesDone, total, lessonDone }.
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const completeRoadmapStage = async ({ lessonId, stage, scorePct }) => {
  const { data } = await api.post('/roadmap/stage', { lessonId, stage, scorePct });
  return data;
};

/* ── Open a section out of order ────────────────────────────────
   POST /api/roadmap/unlock
   Opens a section the learner has not reached yet. Nothing inside it
   is marked done — the rows still have to be worked through.
   Resolves to { unlockedSections }.
   🔒 Requires user token
─────────────────────────────────────────────────────────────── */
export const unlockRoadmapSection = async (sectionId) => {
  const { data } = await api.post('/roadmap/unlock', { sectionId });
  return data;
};

/* ── Clear a section and re-lock it ─────────────────────────────
   POST /api/roadmap/reset
   Undoes an unlock and wipes the section's banked stages and manual
   ticks. Mastery is not touched — see the controller for why. */
export const resetRoadmapSection = async (sectionId) => {
  const { data } = await api.post('/roadmap/reset', { sectionId });
  return data;
};
